import NetInfo from "@react-native-community/netinfo";
import { useSetAtom } from "jotai";
import React, { useEffect, useState } from "react";
import { Platform } from "react-native";
import { RelayEnvironmentProvider } from "react-relay";

import { useRelayPrivateEnvironment } from "@whoosh/relay/src";

import { BiometricSessionGate } from "../../features/BiometricAuthentication";
import { useSchemaVersionMismatchHandler } from "../../features/OtaUpdateOverlay";
import { hasAdminRequestErrorAtom } from "../../features/Session";
import NotConnected from "../../screens/NotConnected";
import { SessionCheckerErrorBoundary } from "./SessionCheckerErrorBoundary";
import { SessionCheckerQueryWrapper } from "./SessionCheckerQueryWrapper";
import { getClubSlugFromPatronPath } from "./getClubSlugFromPatronPath";

/**
 * This ensures we call "useGetCurrentSession" before attempting to render almost ANY
 * navigation components. This allows us to retain our URL navigation - even though
 * our Authenticated navigator depends on that query being called first.
 *
 * It is not almost any because we render the root NavigationContainer first so that we can
 * do some navigation here.
 *
 * Checks for internet connection as well.
 */
export const SessionChecker = () => {
  const schemaVersionMismatchHandler = useSchemaVersionMismatchHandler();
  const setHasAdminRequestError = useSetAtom(hasAdminRequestErrorAtom);
  // This provider owns the root navigator. Changing its environment while
  // navigating unmounts that navigator and discards the destination. ClubScreen
  // selects the current club's environment; this session check uses boot context.
  const [initialClubSlug] = useState(() =>
    Platform.OS === "web" && typeof window !== "undefined"
      ? getClubSlugFromPatronPath(window.location.pathname)
      : undefined
  );
  const PrivateEnvironment = useRelayPrivateEnvironment({
    schemaVersionMismatchHandler,
    clubSlug: initialClubSlug,
    useStoredClubSlug: false,
    onError: (error: any) => {
      if (error?.message === "ADMIN_REQUEST") {
        setHasAdminRequestError(true);
      }
    },
  });
  const [isConnected, setIsConnected] = useState<boolean | null>(true);

  useEffect(() => {
    const unsubscribe = NetInfo.addEventListener((state) => {
      setIsConnected(state.isConnected);
    });

    return () => unsubscribe();
  }, [setIsConnected]);

  if (!isConnected) {
    return <NotConnected />;
  }

  return PrivateEnvironment ? (
    <RelayEnvironmentProvider environment={PrivateEnvironment}>
      <SessionCheckerErrorBoundary>
        <BiometricSessionGate>
          <SessionCheckerQueryWrapper />
        </BiometricSessionGate>
      </SessionCheckerErrorBoundary>
    </RelayEnvironmentProvider>
  ) : null;
};
